import BaseModal from '@/components/BaseModal';
import BoldLargeText from '@/components/BoldLargeText';
import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import CriticalBanner from '@/components/CriticalBanner';
import RegularText from '@/components/RegularText';
import CustomCard from '@/components/customCard';
import Layout from '@/components/layout';
import { LIST_PLAN_ENABLE_LIFETIME } from '@/constants';
import { BREAKPOINT, Plan, Subscription } from '@/constants/enum';
import { checkShowErrorInline } from '@/helpers';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import { useRedirect } from '@/hooks/useRedirect';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { dataSettingsSelector, locationInfoSelector } from '@/redux/slice/settings.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { IResponseApi } from '@/types/apis/response';
import { Badge, Button, ButtonProps, Card, Icon, InlineGrid, SkeletonBodyText } from '@shopify/polaris';
import { CheckIcon, MagicIcon } from '@shopify/polaris-icons';
import { ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import ApplyDiscountModal from './components/ApplyDiscountModal';
import ComparePlans from './components/ComparePlans';
import DowngradeModal from './components/DowngradeModal';
import { PricingStyled } from './styled';

interface IPricingCard {
  plan: Plan;
  title: string;
  badge?: string;
  description: string;
  price: {
    current: string;
    save?: string;
    previous?: string;
    description: string;
    extraDescription?: string;
  };
  button: {
    content: string;
    variant?: ButtonProps['variant'];
  };
  benefits: {
    title?: string;
    contents: (string | ReactNode)[];
  };
}

interface IOpenModal {
  applyDiscount: boolean;
  downgrade: boolean;
  plan: 'free' | 'standard' | 'pro';
}

const Pricing = () => {
  useCustomUserIdClarity('Pricing plan');
  const dispatch = useDispatch();
  const location = useLocation();
  const { isActivePlusPlan } = useUserPlan();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const dataSettings = useSelector(dataSettingsSelector);
  const subscription = dataSettings.subscription;
  const isSkip = useSelector(isSkipApiSelector);
  const locationInfo = useSelector(locationInfoSelector);
  const [error, setError] = useState({ status: false, msg: '' });
  const [isOpenModal, setIsOpenModal] = useState<IOpenModal>({
    applyDiscount: location?.state?.isOpenModalApplyDiscount ?? false,
    downgrade: false,
    plan: 'free',
  });
  const [isOpenModalRemoveDiscount, setIsOpenModalRemoveDiscount] = useState(false);
  const [planType, setPlanType] = useState<Subscription>(dataSettings.subscription || Subscription.Monthly);

  const { data, isFetching } = apiCaller.useGetListPricingQuery(undefined, { skip: isSkip });
  // const { data: dataDiscount, isLoading: isLoadingDiscount } = apiCaller.useGetShopDiscountQuery(undefined, {
  //   skip: isSkip,
  // });
  const [removeDiscount, isLoadingRemove] = apiCaller.useRemoveShopDiscountMutation();
  const [updatePlan, { isLoading }] = apiCaller.useGetUrlUpdatePlanMutation();
  // const trackGA4Event = useTrackGA4Event();
  const { redirectTo } = useRedirect();

  // useEffect(() => {
  //   trackGA4Event(Ga4Event.PricingPlanImpression);
  // }, [trackGA4Event]);

  useEffect(() => {
    setPlanType(subscription);
  }, [subscription]);

  const convertPrice = (price: number): string => {
    if (!price) {
      return 'Free';
    }
    return '$' + price.toFixed(2);
  };

  const getTrialDays = useCallback(
    (currentPlans: IResponseApi.IGetAppPricing['data']) => {
      const currentPlan = currentPlans[0];
      // const shopDiscount = dataDiscount?.data;
      if (!currentPlan) return 0;

      if (
        // !shopDiscount ||
        planType !== Subscription.Monthly
      )
        return currentPlan.trialDays;

      // const plansApply = (shopDiscount?.plansApply?.split(',') as unknown as Plan) || [];
      // if (!plansApply.includes(currentPlan.plan)) return currentPlan?.trialDays;

      // return shopDiscount?.trialDays || currentPlan.trialDays;
      return currentPlan.trialDays;
    },
    // [dataDiscount?.data, planType],
    [planType],
  );

  const pricingCardConfig = useMemo<IPricingCard[]>(() => {
    const prices = [...(data?.data || [])].sort((a, b) => a.order - b.order);
    if (prices && prices.length >= 4) {
      const allPlans = [
        {
          plan: Plan.FREE,
          title: prices[0]?.displayName || '',
          badge: prices[0]?.tag || '',
          description:
            planType === Subscription.Monthly ? prices[0]?.monthlyDescription || '' : prices[0]?.annuallyDescription || '',
          price: {
            current:
              planType === Subscription.Monthly
                ? convertPrice(prices[0].price)
                : convertPrice(prices[0].annuallyMonthlyDisplayPrice),
            save:
              planType === Subscription.Annually && prices[0].annuallyDiscount
                ? `${(prices[0].annuallyDiscount * 100).toFixed(0)}% OFF`
                : undefined,
            previous:
              planType === Subscription.Annually && prices[0].annuallyMonthlyDisplayPrice ? `$${prices[0].price}` : undefined,
            description: 'Forever',
          },
          button: {
            content: 'Select plan',
          },
          benefits: {
            contents: ['Unlimited ETA views', 'Unlimited synced product quota', '1 estimate rule', '24/7 live chat support'],
          },
        },
        {
          plan: Plan.STANDARD,
          title: prices[1]?.displayName || '',
          badge: prices[1]?.tag || '',
          description:
            planType === Subscription.Monthly ? prices[1]?.monthlyDescription || '' : prices[1]?.annuallyDescription || '',
          price: {
            current:
              planType === Subscription.Monthly
                ? convertPrice(
                    // dataDiscount?.data?.fixedPrice ??
                    prices[1].price,
                    // * (1 - +(dataDiscount?.data?.discountValue ?? 0) / 100),
                  )
                : planType === Subscription.Annually
                ? convertPrice(prices[1].annuallyMonthlyDisplayPrice)
                : convertPrice(dataSettings?.chargePrice ? +dataSettings?.chargePrice : 0),
            save:
              planType === Subscription.Annually && prices[1].annuallyDiscount
                ? `${(prices[1].annuallyDiscount * 100).toFixed(0)}% OFF`
                : // : typeof dataDiscount?.data?.fixedPrice !== 'number' && dataDiscount?.data?.discountValue
                  // ? dataDiscount?.data?.discountValue + '% OFF'
                  undefined,
            previous:
              planType === Subscription.Annually && prices[1].annuallyMonthlyDisplayPrice ? `$${prices[1].price}` : undefined,
            description: planType === Subscription.Lifetime ? 'Lifetime' : '/month',
            extraDescription: planType === Subscription.Annually ? 'billed once yearly' : '',
          },
          button: {
            content: (getTrialDays([prices[1]]) || 0) > 0 ? `Start ${getTrialDays([prices[1]])}-day free trial` : 'Select plan',
            variant: 'primary' as const,
          },
          benefits: {
            title: 'Everything in Free, plus',
            contents: [
              '3 estimate rules',
              '3 additional languages',
              'Item conditions (include/exclude products, collections, vendors)',
              'Show EDD in Cart',
              'Custom message when out of stock',
              'Edit visual appearance, timeline icon',
              'Last 7-day analytics',
              '24/7 live chat support',
            ],
          },
        },
        {
          plan: Plan.PRO,
          title: prices[2]?.displayName || '',
          badge: prices[2]?.tag || '',
          description:
            planType === Subscription.Monthly ? prices[2]?.monthlyDescription || '' : prices[2]?.annuallyDescription || '',
          price: {
            current:
              planType === Subscription.Monthly
                ? convertPrice(
                    // dataDiscount?.data?.fixedPrice ??
                    prices[2].price,
                    // * (1 - +(dataDiscount?.data?.discountValue ?? 0) / 100),
                  )
                : planType === Subscription.Annually
                ? convertPrice(prices[2].annuallyMonthlyDisplayPrice)
                : convertPrice(dataSettings?.chargePrice ? +dataSettings?.chargePrice : 0),
            save:
              planType === Subscription.Annually && prices[2].annuallyDiscount
                ? `${(prices[2].annuallyDiscount * 100).toFixed(0)}% OFF`
                : // : typeof dataDiscount?.data?.fixedPrice !== 'number' && dataDiscount?.data?.discountValue
                  // ? dataDiscount?.data?.discountValue + '% OFF'
                  undefined,
            previous:
              planType === Subscription.Annually && prices[2].annuallyMonthlyDisplayPrice ? `$${prices[2].price}` : undefined,
            description: planType === Subscription.Lifetime ? 'Lifetime' : '/month',
            extraDescription: planType === Subscription.Annually ? 'billed once yearly' : '',
          },
          button: {
            content: (getTrialDays([prices[2]]) || 0) > 0 ? `Start ${getTrialDays([prices[2]])}-day free trial` : 'Select plan',
            variant: 'primary' as const,
          },
          benefits: {
            title: 'Everything in Standard, plus',
            contents: [
              'Unlimited estimate rules',
              'Unlimited additional languages',
              'Location conditions (regions, countries, states/provinces, zipcodes)',
              'Auto-detect location',
              'Import zipcode CSV',
              'Zipcode validity',
              locationInfo && locationInfo.data?.country_code === 'IN' ? 'Integrate with Shiprocket' : undefined,
              'Full analytics',
              'Priority 24/7 live chat support',
            ].filter(Boolean),
          },
        },
        {
          plan: Plan.SHOPIFY_PLUS,
          title: prices[3]?.displayName || '',
          badge: prices[3]?.tag || '',
          description:
            planType === Subscription.Monthly ? prices[3]?.monthlyDescription || '' : prices[3]?.annuallyDescription || '',
          price: {
            current:
              planType === Subscription.Monthly
                ? convertPrice(
                    // dataDiscount?.data?.fixedPrice ??
                    prices[3].price,
                    // * (1 - +(dataDiscount?.data?.discountValue ?? 0) / 100),
                  )
                : planType === Subscription.Annually
                ? convertPrice(prices[3].annuallyMonthlyDisplayPrice)
                : convertPrice(dataSettings?.chargePrice ? +dataSettings?.chargePrice : 0),
            save:
              planType === Subscription.Annually && prices[3].annuallyDiscount
                ? `${(prices[3].annuallyDiscount * 100).toFixed(0)}% OFF`
                : // : typeof dataDiscount?.data?.fixedPrice !== 'number' && dataDiscount?.data?.discountValue
                  // ? dataDiscount?.data?.discountValue + '% OFF'
                  undefined,
            previous:
              planType === Subscription.Annually && prices[3].annuallyMonthlyDisplayPrice ? `$${prices[3].price}` : undefined,
            description: planType === Subscription.Lifetime ? 'Lifetime' : '/month',
            extraDescription: planType === Subscription.Annually ? 'billed once yearly' : '',
          },
          button: {
            content: (getTrialDays([prices[3]]) || 0) > 0 ? `Start ${getTrialDays([prices[3]])}-day free trial` : 'Select plan',
            variant: 'primary' as const,
          },
          benefits: {
            title: 'Everything in Pro, plus',
            contents: [
              'Unlimited estimate rules able to customize for thousands of products at once',
              'ETA app block in Checkout powered by UI extensions',
              'Fast handling high traffic store',
              'High priority 24/7 live chat support',
            ],
          },
        },
      ];

      if (planType === Subscription.Lifetime) {
        return allPlans.filter((item) => LIST_PLAN_ENABLE_LIFETIME.includes(item.plan));
      }
      return isActivePlusPlan ? allPlans : allPlans.filter((item) => item.plan !== Plan.SHOPIFY_PLUS);
    }
    return [];
  }, [data?.data, planType, getTrialDays, dataSettings?.chargePrice, locationInfo, isActivePlusPlan]);

  const handleUpdatePlan = (subscription: Subscription, plan: Plan) => {
    setError({ status: false, msg: '' });
    updatePlan({
      plan,
      subscription,
    }).then((res) => {
      const checkError = checkShowErrorInline(res);
      if (checkError.status) {
        setError(checkError);
      } else if ('data' in res && res.data?.data?.url) {
        redirectTo(res.data?.data?.url);
      }
    });
    // trackGA4Event(Ga4Event.ChoosePlan, plan, subscription);
  };

  const handleChangeIsOpenModal = (key: keyof typeof isOpenModal) => (value: boolean) => {
    setIsOpenModal((prev) => ({ ...prev, [key]: value }));
  };

  const handleRemoveDiscount = async () => {
    try {
      const res = await removeDiscount().unwrap();
      if (res.code === 200) {
        dispatch(toastSlice.actions.handleToast({ content: res.message, isOpen: true }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsOpenModalRemoveDiscount(false);
    }
  };

  return (
    <Layout title="Pricing plan" fullWidth={false}>
      <PricingStyled isYearly={planType === Subscription.Annually}>
        {/* <LifetimeBanner /> */}
        <div className="pricing-button-group-container">
          {/* {isLoadingDiscount ? null : ( */}
          <div className="pricing-button-group">
            <Button
              variant={planType === Subscription.Monthly ? undefined : 'tertiary'}
              onClick={() => {
                setPlanType(Subscription.Monthly);
              }}
            >
              Monthly
            </Button>
            <Button
              variant={planType === Subscription.Annually ? undefined : 'tertiary'}
              onClick={() => {
                setPlanType(Subscription.Annually);
              }}
            >
              Yearly (save up to 20%)
            </Button>
            {subscription === Subscription.Lifetime ? (
              <Button
                variant={planType === Subscription.Lifetime ? undefined : 'tertiary'}
                onClick={() => {
                  setPlanType(Subscription.Lifetime);
                }}
              >
                Lifetime
              </Button>
            ) : null}
          </div>
          {/* )} */}
        </div>
        {/* {!isLoadingDiscount && planType === Subscription.Monthly ? (
          <>
            {dataDiscount?.data ? null : (
              <div className="mt-16 text-center">
                <Button
                  onClick={() => {
                    handleChangeIsOpenModal('applyDiscount')(true);
                  }}
                  variant="plain"
                >
                  Apply discount code
                </Button>
              </div>
            )}
            {dataDiscount?.data?.code ? (
              <div className="flex-center text-center mt-8">
                Applied discount:
                <Tooltip
                  content={
                    dataDiscount?.data?.fixedPrice
                      ? '$' + dataDiscount?.data?.fixedPrice
                      : dataDiscount?.data?.discountValue + '%'
                  }
                >
                  <span className="ml-4 wrap-discount-tag">
                    <Tag
                      onRemove={() => {
                        setIsOpenModalRemoveDiscount(true);
                      }}
                    >
                      <div className="d-flex">
                        <Icon source={DiscountIcon} />
                        <span className="ml-4">
                          <Text as="p" fontWeight="semibold">
                            {dataDiscount?.data?.code}
                          </Text>
                        </span>
                      </div>
                    </Tag>
                  </span>
                </Tooltip>
              </div>
            ) : null}
          </>
        ) : null} */}
        <div className="mt-8">
          <CriticalBanner isNotInCard isVisible={error.status}>
            {error.msg}
          </CriticalBanner>
        </div>
        <div className="pricing-card-container">
          <InlineGrid columns={{ xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }} gap={'400'}>
            {pricingCardConfig.length ? (
              (pricingCardConfig.length === 4 ? pricingCardConfig.slice(1, 4) : pricingCardConfig).map((item) => {
                let isPlanSelected = false;
                if (dataSettings.plan === Plan.FREE && item.plan === Plan.FREE) {
                  isPlanSelected = true;
                }
                if (
                  dataSettings.subscription === planType &&
                  (dataSettings.plan === item.plan || (dataSettings.plan === Plan.CHARGE && item.plan === Plan.PRO))
                ) {
                  isPlanSelected = true;
                }

                let currentPrice: string;
                if (!isPlanSelected || dataSettings?.chargePrice === undefined || item.plan === Plan.FREE) {
                  currentPrice = item.price.current;
                } else {
                  currentPrice = convertPrice(
                    planType !== Subscription.Annually ? +dataSettings.chargePrice : +dataSettings.chargePrice / 12,
                  );
                }

                return (
                  <RegularText key={item.title}>
                    <div className="pricing-card">
                      <div>
                        <div className="pricing-card-content">
                          <div className="pricing-card-title">
                            <BoldText>{item.title}</BoldText>
                            {item.badge ? (
                              <span className="ml-8">
                                <Badge tone="info">{item.badge}</Badge>
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-8">{item.description}</div>
                          <div className="pricing-card-price">
                            <BoldLargeText>{currentPrice}</BoldLargeText>
                            {item.price.save ? <div className="save-text">{item.price.save}</div> : null}
                            {item.price.previous ? <div className="previous-price">{item.price.previous}</div> : null}
                          </div>
                          <div className="mt-8">{item.price.description}</div>
                          <div className="price-extra-description">{item.price.extraDescription}</div>
                        </div>
                        <div className="pricing-button">
                          {isPlanSelected ? (
                            <Button disabled fullWidth variant="primary">
                              Current plan
                            </Button>
                          ) : (
                            <Button
                              fullWidth
                              variant={item.button.variant}
                              loading={isLoading || isFetching}
                              onClick={() => {
                                const currentPlan = dataSettings.plan;
                                switch (item.plan) {
                                  case Plan.FREE:
                                    if (currentPlan !== Plan.FREE) {
                                      setIsOpenModal((prev) => ({ ...prev, downgrade: true, plan: 'free' }));
                                    }
                                    break;
                                  case Plan.STANDARD:
                                    if (currentPlan === Plan.PRO || currentPlan === Plan.SHOPIFY_PLUS) {
                                      setIsOpenModal((prev) => ({ ...prev, downgrade: true, plan: 'standard' }));
                                    } else {
                                      handleUpdatePlan(planType, item.plan);
                                    }
                                    break;
                                  case Plan.PRO:
                                    if (currentPlan === Plan.SHOPIFY_PLUS) {
                                      setIsOpenModal((prev) => ({ ...prev, downgrade: true, plan: 'pro' }));
                                    } else {
                                      handleUpdatePlan(planType, item.plan);
                                    }
                                    break;
                                  case Plan.SHOPIFY_PLUS:
                                    if (isActivePlusPlan) {
                                      handleUpdatePlan(planType, item.plan);
                                    }
                                    break;
                                  default:
                                    handleUpdatePlan(planType, item.plan);
                                    break;
                                }
                              }}
                            >
                              {item.button.content}
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="benefits">
                        {item.benefits.title ? (
                          <BoldSmallText>
                            <div className="benefits-title">
                              <Icon source={MagicIcon} />
                              <div>{item.benefits.title}</div>
                            </div>
                          </BoldSmallText>
                        ) : null}
                        {item.benefits.contents.map((benefit, index) => (
                          <div className="benefits-item" key={index}>
                            <Icon tone="success" source={CheckIcon} />
                            <span>{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </RegularText>
                );
              })
            ) : (
              <>
                <Card>
                  <SkeletonBodyText lines={23} />
                </Card>
                <Card>
                  <SkeletonBodyText lines={23} />
                </Card>
                <Card>
                  <SkeletonBodyText lines={23} />
                </Card>
              </>
            )}
          </InlineGrid>

          <div className="mt-16">
            {pricingCardConfig.length && pricingCardConfig.length === 4 ? (
              <CustomCard
                title={pricingCardConfig[0]?.title}
                secondaryActions={{
                  content: dataSettings.plan === pricingCardConfig[0].plan ? 'Current plan' : pricingCardConfig[0].button.content,
                  onAction: () => {
                    if ((dataSettings.plan === pricingCardConfig[0].plan && dataSettings.plan) !== Plan.FREE) {
                      setIsOpenModal((prev) => ({ ...prev, downgrade: true, plan: 'free' }));
                    }
                  },
                  buttonProps: {
                    variant: dataSettings.plan === pricingCardConfig[0].plan ? 'primary' : pricingCardConfig[0].button.variant,
                    loading: dataSettings.plan === pricingCardConfig[0].plan ? undefined : isLoading || isFetching,
                    disabled: dataSettings.plan === pricingCardConfig[0].plan,
                  },
                }}
              >
                <div className="mb-8">
                  <RegularText>{pricingCardConfig[0]?.description}</RegularText>
                </div>
                <div className={`benefits ${isMobile ? 'flex-column' : 'benefits__horizontal'}`}>
                  {pricingCardConfig[0]?.benefits?.contents.map((benefit, index) => (
                    <div className="benefits-item" key={index}>
                      <Icon tone="success" source={CheckIcon} />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </CustomCard>
            ) : null}
          </div>
        </div>
        <div className="mt-16">
          <ComparePlans />
        </div>
        {/* <div className="mt-32">
          <Reviews />
        </div> */}
        {/* <div className="mt-32">
          <FAQs />
        </div> */}
      </PricingStyled>
      <ApplyDiscountModal open={isOpenModal.applyDiscount} onClose={() => handleChangeIsOpenModal('applyDiscount')(false)} />
      <DowngradeModal
        plan={isOpenModal.plan}
        open={isOpenModal.downgrade}
        onClose={() => handleChangeIsOpenModal('downgrade')(false)}
        onConfirm={() => handleUpdatePlan(planType, isOpenModal.plan as Plan)}
      />
      <BaseModal
        open={isOpenModalRemoveDiscount}
        onClose={() => setIsOpenModalRemoveDiscount(false)}
        title="Remove discount"
        loading={isLoadingRemove.isLoading}
        primaryAction={{
          content: 'Cancel',
          onAction: () => setIsOpenModalRemoveDiscount(false),
        }}
        secondaryActions={[
          {
            content: 'Confirm',
            onAction: handleRemoveDiscount,
            disabled: isLoadingRemove.isLoading,
            loading: isLoadingRemove.isLoading,
          },
        ]}
      >
        Do you want to remove discount?
      </BaseModal>
    </Layout>
  );
};

export default memo(Pricing);
